import React, { useState, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, Gift, Clock, ShieldCheck, ChevronDown, ChevronUp, BriefcaseMedical, ClipboardList, DoorOpen } from 'lucide-react';
import { Button } from './ui/Button';
import { trackAddToCart } from './utils/metaPixel';
function ReadMore({
  children
}: {
  children: React.ReactNode;
}) {
  const [expanded, setExpanded] = useState(false);
  return <div data-id="element-17">
      <div className={expanded ? '' : 'max-h-[4.5rem] overflow-hidden relative'} data-id="element-18">
        {children}
        {!expanded && <div className="absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-neutral-950 to-transparent pointer-events-none" data-id="element-19" />}
      </div>
      <button onClick={() => setExpanded(!expanded)} className="mt-2 text-brand font-semibold text-sm flex items-center gap-1 hover:underline" data-id="element-20">
        {expanded ? <>
            Leer menos <ChevronUp size={14} data-id="element-21" />
          </> : <>
            Leer más <ChevronDown size={14} data-id="element-22" />
          </>}
      </button>
    </div>;
}
function BonusBullet({
  text
}: {
  text: string;
}) {
  return <li className="flex items-start gap-3" data-id="element-23">
      <CheckCircle2 className="text-brand shrink-0 mt-0.5" size={18} data-id="element-24" />
      <span className="text-neutral-300" data-id="element-25">{text}</span>
    </li>;
}
export function BonusSection() {
  return <div className="bg-neutral-950" data-id="element-26">
      {/* BLOQUE 1 — TRANSICIÓN */}
      <section className="py-8 md:py-14 px-4 bg-neutral-900 border-y border-neutral-800" data-id="element-27">
        <div className="container mx-auto max-w-3xl text-center" data-id="element-28">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="space-y-3" data-id="element-29">
            <p className="text-xl md:text-2xl font-display text-white" data-id="element-30">
              Pero antes de que hagas clic, quiero que sepas algo.
            </p>
            <p className="text-base md:text-lg text-neutral-300" data-id="element-31">
              No quería que te llevarás solo la guía.
            </p>
            <p className="text-neutral-400" data-id="element-32">
              Porque la realidad es que una guía sin las herramientas correctas
              para implementarla es como tener un mapa sin saber dónde estás
              parado.
            </p>
            <p className="text-neutral-400" data-id="element-33">
              Así que decidí agregar tres recursos extra que creé
              específicamente para que el Protocolo Semáforo funcione de verdad
              en una emergencia real.
            </p>
            <div className="pt-4 space-y-2 text-neutral-300 font-medium" data-id="element-34">
              <p data-id="element-35">No son relleno.</p>
              <p data-id="element-36">No son PDFs genéricos que encontrás gratis en internet.</p>
              <p data-id="element-37">
                Son exactamente lo que necesitás tener listo antes de que llegue
                el momento.
              </p>
            </div>
            <p className="text-brand font-bold text-lg pt-2" data-id="element-38">
              Y los tres son tuyos sin costo adicional.
            </p>
          </motion.div>
        </div>
      </section>

      {/* BLOQUE 2 — INTRO AL STACK */}
      <section className="py-8 md:py-12 px-4 bg-neutral-950" data-id="element-39">
        <div className="container mx-auto max-w-4xl text-center" data-id="element-40">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-41">
            <h2 className="text-3xl md:text-5xl font-display text-white mb-2" data-id="element-42">
              Cuando hacés tu pedido hoy,{' '}
              <span className="text-brand" data-id="element-43">recibís esto también:</span>
            </h2>
            <p className="text-lg md:text-xl text-neutral-400" data-id="element-44">
              Tres recursos de acción inmediata para que nunca más improvises en
              una emergencia felina.
            </p>
          </motion.div>
        </div>
      </section>

      {/* BLOQUE 3 — BONO 1 */}
      <section className="py-6 md:py-12 px-4 bg-neutral-900" data-id="element-45">
        <div className="container mx-auto max-w-5xl" data-id="element-46">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="bg-neutral-950 border border-neutral-800 rounded-2xl overflow-hidden" data-id="element-47">
            <div className="grid grid-cols-1 md:grid-cols-2" data-id="element-48">
              <div className="p-6 md:p-8 flex flex-col justify-center" data-id="element-49">
                <div className="inline-flex items-center gap-2 bg-brand/10 text-brand text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-wider w-fit mb-4" data-id="element-50">
                  <Gift size={14} data-id="element-51" />
                  BONO #1 — ACCESO INMEDIATO
                </div>
                <h3 className="text-3xl font-display text-white mb-2" data-id="element-52">
                  Botiquín Felino Express
                </h3>
                <p className="text-brand font-medium mb-4" data-id="element-53">
                  Lo mínimo que tenés que tener listo antes de que pase algo.
                </p>

                <ReadMore data-id="element-54">
                  <div className="space-y-3 text-neutral-400" data-id="element-55">
                    <p data-id="element-56">
                      La mayoría de los dueños de gatos creen que van a
                      reaccionar bien en una emergencia.
                    </p>
                    <p data-id="element-57">
                      Y después llega el momento, buscan gasas en tres cajones
                      distintos, no encuentran nada útil, y terminan
                      improvisando con lo primero que aparece.
                    </p>
                    <p data-id="element-58">El Botiquín Felino Express resuelve eso hoy.</p>
                    <p data-id="element-59">
                      En menos de 20 minutos vas a saber exactamente qué tener
                      en casa, en qué orden comprarlo si te falta algo y qué
                      cosas que creías útiles en realidad pueden complicar más
                      la situación.
                    </p>
                    <p data-id="element-60">No es una lista genérica.</p>
                    <p data-id="element-61">
                      Es el inventario mínimo viable que necesita cualquier
                      hogar con un gato en Argentina.
                    </p>
                  </div>
                  <ul className="space-y-3 mt-4" data-id="element-62">
                    <BonusBullet text="La lista maestra completa de lo que debe tener tu botiquín, explicada elemento por elemento" data-id="element-63" />
                    <BonusBullet text="Prioridad de compra si hoy no podés armar todo junto" data-id="element-64" />
                    <BonusBullet text="Qué nunca deberías guardar ni usar con un gato aunque te parezca lógico" data-id="element-65" />
                    <BonusBullet text="Cómo organizarlo para encontrarlo en segundos y no en minutos" data-id="element-66" />
                    <BonusBullet text="Checklist imprimible para tildar lo que ya tenés y comprar solo lo que falta" data-id="element-67" />
                    <BonusBullet text="Hoja de farmacia para ir directo a comprar sin pensar" data-id="element-68" />
                  </ul>
                  <p className="text-neutral-300 mt-4 text-sm italic" data-id="element-69">
                    Si terminás de leer la guía hoy y armás el botiquín esta
                    semana, ya estás en una situación completamente diferente a
                    la de la mayoría de los dueños de gatos.
                  </p>
                </ReadMore>

                <div className="mt-4 pt-4 border-t border-neutral-800" data-id="element-70">
                  <p className="text-neutral-500 text-sm" data-id="element-71">
                    Valor real:{' '}
                    <span className="text-white font-bold line-through" data-id="element-72">
                      $6.700 ARS
                    </span>{' '}
                    —{' '}
                    <span className="text-brand font-bold" data-id="element-73">
                      Incluido sin costo adicional
                    </span>
                  </p>
                </div>
              </div>
              <div className="bg-neutral-900 flex items-center justify-center p-6" data-id="element-74">
                <img src="https://cdn.magicpatterns.com/uploads/s6qAfuPWzMHLKzRnQ7pXMr/Bono_1.png" alt="Botiquín Felino Express" className="w-full max-w-sm h-auto object-contain rounded-lg" data-id="element-75" />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* BLOQUE 4 — BONO 2 */}
      <section className="py-6 md:py-12 px-4 bg-neutral-950" data-id="element-76">
        <div className="container mx-auto max-w-5xl" data-id="element-77">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="bg-neutral-900 border border-neutral-800 rounded-2xl overflow-hidden" data-id="element-78">
            <div className="grid grid-cols-1 md:grid-cols-2" data-id="element-79">
              <div className="bg-neutral-950 flex items-center justify-center p-6 order-2 md:order-1" data-id="element-80">
                <img src="https://cdn.magicpatterns.com/uploads/x5JFDXYyddBBk6UHhUqQAG/Bono_2.png" alt="Ficha de Emergencia para Pegar en la Heladera" className="w-full max-w-sm h-auto object-contain rounded-lg" data-id="element-81" />
              </div>
              <div className="p-6 md:p-8 flex flex-col justify-center order-1 md:order-2" data-id="element-82">
                <div className="inline-flex items-center gap-2 bg-brand/10 text-brand text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-wider w-fit mb-4" data-id="element-83">
                  <Gift size={14} data-id="element-84" />
                  BONO #2 — ACCESO INMEDIATO
                </div>
                <h3 className="text-3xl font-display text-white mb-2" data-id="element-85">
                  Ficha de Emergencia para Pegar en la Heladera
                </h3>
                <p className="text-brand font-medium mb-4" data-id="element-86">
                  La hoja que te ahorra minutos cuando más los necesitás.
                </p>

                <ReadMore data-id="element-87">
                  <div className="space-y-3 text-neutral-400" data-id="element-88">
                    <p data-id="element-89">En una urgencia real, la memoria falla.</p>
                    <p data-id="element-90">
                      No porque seas descuidado. Sino porque así funciona el
                      cerebro humano bajo presión.
                    </p>
                    <p data-id="element-91">
                      Dudás del peso de tu gato. No recordás el nombre del
                      medicamento que toma. No encontrás el número de la
                      guardia.
                    </p>
                    <p data-id="element-92">
                      Y perdés minutos intentando acordarte de todo al mismo
                      tiempo.
                    </p>
                    <p data-id="element-93">
                      La Ficha de Emergencia existe para que ninguna de esas
                      cosas te pase.
                    </p>
                    <p data-id="element-94">
                      Es una plantilla completa, pensada para completar hoy en
                      calma, y tener lista para cuando no la tengas.
                    </p>
                    <p data-id="element-95">
                      Imprimila, pegala en la heladera y sacale una foto para
                      tenerla en el celular.
                    </p>
                    <p data-id="element-96">
                      Así, cuando llegue un momento de pánico, la información ya
                      está ahí. Visible. Ordenada. Lista para ser usada.
                    </p>
                  </div>
                  <ul className="space-y-3 mt-4" data-id="element-97">
                    <BonusBullet text="Datos básicos del gato listos para no pensar de memoria" data-id="element-98" />
                    <BonusBullet text="Historial médico simplificado que el veterinario necesita saber primero" data-id="element-99" />
                    <BonusBullet text="Contactos urgentes incluyendo guardia 24 hs y personas de apoyo" data-id="element-100" />
                    <BonusBullet text="Campos de monitoreo en tiempo real para completar en el momento" data-id="element-101" />
                    <BonusBullet text="Versión para imprimir y pegar en heladera, puerta o botiquín" data-id="element-102" />
                    <BonusBullet text="Versión móvil para tenerla siempre encima aunque salgas apurado" data-id="element-103" />
                  </ul>
                  <p className="text-neutral-300 mt-4 text-sm italic" data-id="element-104">
                    Esta ficha no parece espectacular. Pero cuando llegue el
                    momento, puede ser la herramienta más útil de toda tu casa.
                  </p>
                </ReadMore>

                <div className="mt-4 pt-4 border-t border-neutral-800" data-id="element-105">
                  <p className="text-neutral-500 text-sm" data-id="element-106">
                    Valor real:{' '}
                    <span className="text-white font-bold line-through" data-id="element-107">
                      $5.500 ARS
                    </span>{' '}
                    —{' '}
                    <span className="text-brand font-bold" data-id="element-108">
                      Incluido sin costo adicional
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* BLOQUE 5 — BONO 3 */}
      <section className="py-6 md:py-12 px-4 bg-neutral-900" data-id="element-109">
        <div className="container mx-auto max-w-5xl" data-id="element-110">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="bg-neutral-950 border border-neutral-800 rounded-2xl overflow-hidden" data-id="element-111">
            <div className="grid grid-cols-1 md:grid-cols-2" data-id="element-112">
              <div className="p-6 md:p-8 flex flex-col justify-center" data-id="element-113">
                <div className="inline-flex items-center gap-2 bg-brand/10 text-brand text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-wider w-fit mb-4" data-id="element-114">
                  <Gift size={14} data-id="element-115" />
                  BONO #3 — ACCESO INMEDIATO
                </div>
                <h3 className="text-3xl font-display text-white mb-2" data-id="element-116">
                  Checklist 60 Segundos Antes de Salir a la Guardia
                </h3>
                <p className="text-brand font-medium mb-4" data-id="element-117">
                  Lo que tenés que revisar antes de abrir la puerta.
                </p>

                <ReadMore data-id="element-118">
                  <div className="space-y-3 text-neutral-400" data-id="element-119">
                    <p data-id="element-120">
                      Salir rápido importa. Pero salir en orden importa igual.
                    </p>
                    <p data-id="element-121">
                      En el apuro, hay errores que casi todos cometen: Salen sin
                      confirmar a qué guardia van. Se olvidan el celular o se
                      van sin batería. Llegan sin poder explicar qué pasó. No
                      saben qué decir primero y pierden tiempo vital en la
                      recepción.
                    </p>
                    <p data-id="element-122">
                      Esta checklist te da una rutina de salida de 60 segundos
                      para que nada de eso te pase.
                    </p>
                    <p data-id="element-123">
                      Tiene cuatro bloques simples: qué revisar en el gato, qué
                      llevar vos, qué decir al llegar y qué no hacer por pánico.
                    </p>
                    <p data-id="element-124">
                      No está hecha para que pienses demasiado. Está hecha para
                      que la mires y salgas.
                    </p>
                    <p data-id="element-125">
                      Y si no tenés ni 60 segundos, tiene un modo ultra rápido
                      de cuatro pasos que podés hacer en 15.
                    </p>
                  </div>
                  <ul className="space-y-3 mt-4" data-id="element-126">
                    <BonusBullet text="Checklist del gato para asegurarte de que sale contenido y lo más estable posible" data-id="element-127" />
                    <BonusBullet text="Checklist de lo que llevás para no olvidarte nada en el apuro" data-id="element-128" />
                    <BonusBullet text="Fórmula de comunicación para explicar rápido y bien al llegar a la guardia" data-id="element-129" />
                    <BonusBullet text="Lista de lo que NO hacer para evitar errores comunes bajo presión" data-id="element-130" />
                    <BonusBullet text="Modo ultra rápido de 4 pasos para cuando no hay margen de tiempo" data-id="element-131" />
                    <BonusBullet text="Versión imprimible para pegar cerca de la puerta o la transportadora" data-id="element-132" />
                  </ul>
                  <p className="text-neutral-300 mt-4 text-sm italic" data-id="element-133">
                    Pegala cerca de la salida hoy. Leela una vez. Y la próxima
                    vez que la necesites, tu cerebro ya va a saber qué hacer.
                  </p>
                </ReadMore>

                <div className="mt-4 pt-4 border-t border-neutral-800" data-id="element-134">
                  <p className="text-neutral-500 text-sm" data-id="element-135">
                    Valor real:{' '}
                    <span className="text-white font-bold line-through" data-id="element-136">
                      $5.000 ARS
                    </span>{' '}
                    —{' '}
                    <span className="text-brand font-bold" data-id="element-137">
                      Incluido sin costo adicional
                    </span>
                  </p>
                </div>
              </div>
              <div className="bg-neutral-900 flex items-center justify-center p-6" data-id="element-138">
                <img src="https://cdn.magicpatterns.com/uploads/cZ7u1mFYZXmvzft4CC12eV/Bono_3.png" alt="Checklist 60 Segundos Antes de Salir a la Guardia" className="w-full max-w-sm h-auto object-contain rounded-lg" data-id="element-139" />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* BLOQUE 6 — RESUMEN DEL STACK */}
      <section className="py-6 md:py-8 px-4 bg-neutral-950" data-id="element-140">
        <div className="container mx-auto max-w-3xl" data-id="element-141">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-142">
            <h2 className="text-2xl md:text-4xl font-display text-white text-center mb-4" data-id="element-143">
              Todo lo que recibís hoy:
            </h2>

            <div className="bg-neutral-900 border border-neutral-800 rounded-xl overflow-hidden" data-id="element-144">
              <table className="w-full text-left text-sm" data-id="element-145">
                <tbody data-id="element-146">
                  <tr className="border-b border-neutral-800" data-id="element-147">
                    <td className="px-4 py-2.5 w-8" data-id="element-148">
                      <CheckCircle2 className="text-brand" size={16} data-id="element-149" />
                    </td>
                    <td className="px-4 py-2.5 text-white font-medium" data-id="element-150">
                      Protocolo Semáforo de 60 Segundos
                    </td>
                    <td className="px-4 py-2.5 text-neutral-400 text-right whitespace-nowrap" data-id="element-151">
                      $9.499
                    </td>
                  </tr>
                  <tr className="border-b border-neutral-800" data-id="element-152">
                    <td className="px-4 py-2.5" data-id="element-153">
                      <Gift className="text-brand" size={16} data-id="element-154" />
                    </td>
                    <td className="px-4 py-2.5 text-white font-medium" data-id="element-155">
                      Botiquín Felino Express
                    </td>
                    <td className="px-4 py-2.5 text-neutral-400 text-right whitespace-nowrap" data-id="element-156">
                      $6.700
                    </td>
                  </tr>
                  <tr className="border-b border-neutral-800" data-id="element-157">
                    <td className="px-4 py-2.5" data-id="element-158">
                      <Gift className="text-brand" size={16} data-id="element-159" />
                    </td>
                    <td className="px-4 py-2.5 text-white font-medium" data-id="element-160">
                      Ficha de Emergencia
                    </td>
                    <td className="px-4 py-2.5 text-neutral-400 text-right whitespace-nowrap" data-id="element-161">
                      $5.500
                    </td>
                  </tr>
                  <tr className="border-b border-neutral-800" data-id="element-162">
                    <td className="px-4 py-2.5" data-id="element-163">
                      <Gift className="text-brand" size={16} data-id="element-164" />
                    </td>
                    <td className="px-4 py-2.5 text-white font-medium" data-id="element-165">
                      Checklist 60 Segundos
                    </td>
                    <td className="px-4 py-2.5 text-neutral-400 text-right whitespace-nowrap" data-id="element-166">
                      $5.000
                    </td>
                  </tr>
                </tbody>
                <tfoot data-id="element-167">
                  <tr className="border-b border-neutral-800 bg-neutral-800/30" data-id="element-168">
                    <td className="px-4 py-2.5" data-id="element-169"></td>
                    <td className="px-4 py-2.5 text-neutral-400 font-bold" data-id="element-170">
                      Valor total
                    </td>
                    <td className="px-4 py-2.5 text-white font-bold text-right line-through" data-id="element-171">
                      $26.699
                    </td>
                  </tr>
                  <tr className="bg-brand/10" data-id="element-172">
                    <td className="px-4 py-3" data-id="element-173"></td>
                    <td className="px-4 py-3 text-white font-bold text-lg" data-id="element-174">
                      Tu inversión hoy
                    </td>
                    <td className="px-4 py-3 text-brand font-bold text-xl text-right" data-id="element-175">
                      $9.499 ARS
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </motion.div>
        </div>
      </section>

      {/* BLOQUE 7 — JUSTIFICACIÓN EMOCIONAL */}
      <section className="py-8 md:py-12 px-4 bg-neutral-900" data-id="element-176">
        <div className="container mx-auto max-w-3xl" data-id="element-177">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center space-y-3" data-id="element-178">
            <p className="text-xl md:text-2xl font-display text-white" data-id="element-179">
              Mirá ese número un momento.
            </p>
            <p className="text-lg text-neutral-300" data-id="element-180">
              $26.699 ARS en recursos. Por{' '}
              <span className="text-brand font-bold" data-id="element-181">$9.499 ARS</span>.
            </p>
            <p className="text-neutral-400" data-id="element-182">
              Pero más allá del número, lo que me importa que entiendas es esto:
            </p>
            <p className="text-neutral-300 font-medium" data-id="element-183">
              Todo lo que está en este pack fue construido pensando en un solo
              momento.
            </p>
            <p className="text-neutral-400" data-id="element-184">
              Ese momento en el que algo pasa con tu gato a las 3 de la mañana y
              no sabés qué hacer.
            </p>
            <p className="text-neutral-400" data-id="element-185">
              Ese momento en el que el pánico te bloquea y necesitás un sistema,
              no un buscador.
            </p>
            <p className="text-neutral-400" data-id="element-186">
              Ese momento en el que los minutos se escapan y vos todavía estás
              buscando el número de la guardia en viejos chats de WhatsApp.
            </p>
            <div className="pt-4 border-t border-neutral-800 mt-6" data-id="element-187">
              <p className="text-neutral-300" data-id="element-188">
                Este pack no te promete que todo va a salir bien.
              </p>
              <p className="text-white font-bold text-lg mt-2" data-id="element-189">
                Te promete algo más honesto: que cuando llegue ese momento, no
                vas a estar empezando desde cero.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* BLOQUE 8 — URGENCIA Y ESCASEZ */}
      <section className="py-8 md:py-12 px-4 bg-neutral-950" data-id="element-190">
        <div className="container mx-auto max-w-3xl" data-id="element-191">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="bg-brand/10 border border-brand/20 rounded-xl p-5 md:p-8" data-id="element-192">
            <div className="flex items-start gap-4" data-id="element-193">
              <div className="bg-brand/20 p-2 rounded-full shrink-0" data-id="element-194">
                <Clock className="text-brand" size={24} data-id="element-195" />
              </div>
              <div className="space-y-3" data-id="element-196">
                <h3 className="text-xl md:text-2xl font-display text-white" data-id="element-197">
                  Estos bonos son parte del lanzamiento inicial.
                </h3>
                <p className="text-neutral-300" data-id="element-198">
                  Los tres bonos están disponibles porque estamos en la etapa de
                  lanzamiento de esta guía.
                </p>
                <p className="text-neutral-400" data-id="element-199">
                  No sé hasta cuándo van a estar incluidos sin costo adicional.
                  Lo que sí sé es que una vez que los retire del pack, van a
                  estar disponibles por separado o directamente no van a estar.
                </p>
                <p className="text-white font-medium" data-id="element-200">
                  Si estás leyendo esto y los tres siguen acá, es porque todavía
                  podés llevártelos.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* BLOQUE 9 — CTA FINAL */}
      <section className="py-10 md:py-14 px-4 bg-neutral-900 border-t border-neutral-800" data-id="element-201">
        <div className="container mx-auto max-w-4xl text-center" data-id="element-202">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="space-y-6" data-id="element-203">
            <h2 className="text-3xl md:text-5xl font-display text-white" data-id="element-204">
              Tu gato no puede esperar a que{' '}
              <span className="text-brand" data-id="element-205">"lo pienses mejor"</span>.
            </h2>
            <div className="max-w-2xl mx-auto space-y-2 text-neutral-400 text-base md:text-lg" data-id="element-206">
              <p data-id="element-207">
                El mejor momento para tener este pack listo era antes de
                necesitarlo.
              </p>
              <p className="text-white font-medium" data-id="element-208">
                El segundo mejor momento es ahora.
              </p>
              <p data-id="element-209">
                No porque haya una oferta que se acaba mañana. Sino porque no
                sabés lo que puede pasar mañana.
              </p>
              <p className="text-neutral-300" data-id="element-210">
                Y si llegara a pasar algo esta noche, ¿preferís tenerlo o no
                tenerlo?
              </p>
            </div>

            <div className="pt-6" data-id="element-211">
              <Button href="https://amigospeludosarg.shop/cart/49119681052914:1" size="xl" className="w-full md:w-auto" onClick={trackAddToCart} data-id="element-212">
                ¡QUIERO EL PROTOCOLO Y LOS TRES BONOS AHORA!
              </Button>
              <div className="mt-4 space-y-1 text-sm text-neutral-500" data-id="element-213">
                <p data-id="element-214">Acceso inmediato por email tras el pago.</p>
                <p data-id="element-215">Descarga en PDF. Sin suscripciones. Sin letras chicas.</p>
              </div>
            </div>

            <div className="mt-8 bg-neutral-950 border border-neutral-800 rounded-xl p-6 max-w-2xl mx-auto" data-id="element-216">
              <div className="flex items-center justify-center gap-2 mb-3" data-id="element-217">
                <ShieldCheck className="text-brand" size={20} data-id="element-218" />
                <span className="text-white font-bold" data-id="element-219">Garantía de 7 días</span>
              </div>
              <p className="text-neutral-400 text-sm" data-id="element-220">
                Si en los próximos 7 días no sentís que esta guía y estos bonos
                te dieron más claridad y seguridad de la que tenías antes, te
                devuelvo el dinero sin preguntas. Porque una herramienta que no
                sirve no merece quedarse con tu plata.
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>;
}